import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Table } from "react-bootstrap";
import { FaHospital, FaUserMd, FaUserTie, FaFlask } from "react-icons/fa";
import demoImage from "../../assets/images/patientbed.png";
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Dashboard = () => {
  const { user } = useAuth();
  const token = localStorage.getItem("token") || "";

    const navigate = useNavigate();

  useEffect(() => {
    // Add an extra entry to browser history
    window.history.pushState(null, "", window.location.href);

    const handleBack = () => {
      window.history.go(1); // prevent back
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, []);

  const [metrics, setMetrics] = useState({
    totalSubHospitals: 0,
    totalDoctors: 0,
    totalReceptionists: 0,
    totalLabs: 0,
  });

  const [hospitalData, setHospitalData] = useState([]); // table data

  // Fetch dashboard metrics
  const fetchMetrics = async (hospitalId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hospital_id: hospitalId },
      });

      if (response.data) { 
        setMetrics({
          totalSubHospitals: response.data.subHospitals || 0,
          totalDoctors: response.data.doctors || 0,
          totalReceptionists: response.data.receptionists || 0,
          totalLabs: response.data.labs || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard metrics:", error);
    }
  };

  // Fetch hospital-wise breakdown for table
  const fetchHospitalStats = async (hospitalId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/${hospitalId}/subhospitals/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
          
        }
        
      );

      if (response.data) { 
        setHospitalData(response.data.sub_hospitals || []);
     
      }
    } catch (error) {
      console.error("Error fetching hospital stats:", error);
    }
  };

  useEffect(() => {
    if (user && user.hospital_id) {
      fetchMetrics(user.hospital_id);
      fetchHospitalStats(user.hospital_id);
    }
  }, [user]);

  return (
    <div className="dashboard-container p-4" style={{ background: "#f8f9fa" }}>
      {/* Header */}
      <Card className="dashboard-header p-4 shadow-sm mb-4">
        <Row className="align-items-center">
          <Col md={8}>
            <h5 className="mb-1">Good Morning,</h5>
            <h2 className="fw-bold welcome-text">
              Welcome, {user?.name || "Admin"}!
            </h2>
            <p className="text-muted">
              Manage your hospital, sub-hospitals, doctors, receptionists, and
              labs at a glance.
            </p>
          </Col>
          <Col md={4} className="text-center">
            <img
              src={demoImage}
              alt="Dashboard"
              className="header-image"
              style={{ maxHeight: "120px" }}
            />
          </Col>
        </Row>
      </Card>

      {/* Stat Cards */}
      <Row className="g-4 mb-4">
        {/* Sub-Hospitals */}
        <Col md={3} sm={6}>
          <Card className="stat-card shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="vertical-line me-3"></div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="stat-count">
                    {metrics.totalSubHospitals}
                  </span>
                  <FaHospital className="stat-icon" />
                </div>
                <h6 className="mt-3 mb-0 fw-bold">Sub-Hospitals</h6>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Doctors */}
        <Col md={3} sm={6}>
          <Card className="stat-card shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="vertical-line me-3"></div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="stat-count">{metrics.totalDoctors}</span>
                  <FaUserMd className="stat-icon" />
                </div>
                <h6 className="mt-3 mb-0 fw-bold">Doctors</h6>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Receptionists */}
        <Col md={3} sm={6}>
          <Card className="stat-card shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="vertical-line me-3"></div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="stat-count">
                    {metrics.totalReceptionists}
                  </span>
                  <FaUserTie className="stat-icon" />
                </div>
                <h6 className="mt-3 mb-0 fw-bold">Receptionists</h6>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Labs */}
        <Col md={3} sm={6}>
          <Card className="stat-card shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="vertical-line me-3"></div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="stat-count">{metrics.totalLabs}</span>
                  <FaFlask className="stat-icon" />
                </div>
                <h6 className="mt-3 mb-0 fw-bold">Labs</h6>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card className="p-3 shadow-sm">
        <h5 className="mb-3 fw-bold primary-class">Hospitals Overview</h5>
        <Table striped bordered hover responsive className="mb-0">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Doctors</th>
              <th>Receptionists</th>
              <th>Labs</th>
            </tr>
          </thead>
          <tbody>
            {hospitalData.length > 0 ? (
              hospitalData.map((hospital, index) => (
                <tr key={hospital.sub_hospital_id}>
                  <td>{index + 1}</td>
                  <td>{hospital.sub_hospital_name}</td>
                  <td>{hospital.total_doctors || 0}</td>
                  <td>{hospital.total_receptionists || 0}</td>
                  <td>{hospital.total_labs || 0}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted">
                  No hospital data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>
    </div>
  );
};

export default Dashboard;
